import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface DeleteCredentialInput {
  credentialId: string
  organizationId: Organization["id"]
}

export interface DeleteCredentialResponse {
  credentialId?: string
  id?: string
}

export const useDeleteCredential = (
  options?: UseMutationOptions<
    DeleteCredentialResponse,
    HttpError,
    DeleteCredentialInput
  >
) => {
  const { invalidateQueries } = useInvalidateQueries()

  const mutation = useMutation<
    DeleteCredentialResponse,
    HttpError,
    DeleteCredentialInput
  >({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({
      credentialId,
      organizationId,
    }: DeleteCredentialInput) => {
      try {
        const response = await httpClient.delete<DeleteCredentialResponse>(
          `/api/v1/organizations/${organizationId}/pipelines/credentials/${credentialId}`
        )
        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (...args) => {
      options?.onError?.(...args)
    },
    onSuccess: (data, ...rest) => {
      invalidateQueries([createQueryKey(ApiQueryName.Credentials, "getMany")])

      options?.onSuccess?.(data, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "deleteCredential")
}
